import { createContext, useContext, useEffect, useState } from 'react';
import ManagementService from 'services/management';
import { useAuth } from 'context/authContext';

let ManagementContext = createContext({
	getOwnUser: () => ({}),
	setUserCurrentManagement: () => ({}),
	hasSelected: () => ({}),
	user: null,
	isLoading: true,
	hasAgreed: () => false,
});

const useManagement = () => useContext(ManagementContext);

function ManagementProvider({ children }) {
	let [user, setUser] = useState();
	let [isLoading, setIsLoading] = useState(true);
	const { isAuthenticated, user: authUser } = useAuth();

	useEffect(() => {
		if (!isAuthenticated()) return;

		getOwnUser();
	}, [authUser]);

	const getOwnUser = async () => {
		setIsLoading(true);
		setUser(await ManagementService.getOwnUser());
		setIsLoading(false);
	};

	const hasSelected = () => {
		const isSelected = !!user?.currentManagement && user?.currentManagement !== 'NOT_SELECTED';
		return isSelected;
	};

	const setUserCurrentManagement = async (management) => {
		// 1. Set loading to true at the very beginning.
		setIsLoading(true);

		try {
			// 2. Check if the first action is needed.
			if (!hasAgreed()) {
				// 'await' pauses the function until the promise resolves.
				const userAfterAgreement = await ManagementService.acceptAgreements();
				setUser(userAfterAgreement);
			}

			// 3. Now, check if the second action is needed.
			if (!hasSelected()) {
				// 'await' pauses again if this call is necessary.
				const userAfterSelection = await ManagementService.setUserCurrentManagement(management);
				setUser(userAfterSelection);
			}
		} catch (error) {
			// It's good practice to catch potential errors from your service calls.
			console.error('Failed to update user management:', error);
			// You could set an error state here to show a message to the user.
		} finally {
			// 4. This 'finally' block will run AFTER everything in the 'try'
			// block is finished, or if an error was caught.
			// This is the correct place to turn off the loading indicator.
			setIsLoading(false);
		}
	};

	const hasAgreed = () => {
		return Boolean(user?.termsConditionsAcceptedAt && user?.privacyPolicyAcceptedAt);
	};

	const value = { getOwnUser, setUserCurrentManagement, hasSelected, user, isLoading, hasAgreed };

	return <ManagementContext.Provider value={value}>{children}</ManagementContext.Provider>;
}

export { useManagement, ManagementProvider };
